import dayjs from 'dayjs';
import {
  OCRActivityLog,
  OCRActivityLogsResponse,
} from '@/service/gpu-instance/ocr-type';

// Document types for mock data
const DOCUMENT_TYPES = [
  'Thai ID Card',
  'Thai Passport',
  'Driver License',
  'Bank Statement',
  'Tax Document',
  'Business Registration',
  'Medical Record',
  'Invoice',
  'Receipt',
  'Contract',
];

// Sample file names
const FILE_NAMES = [
  'id_card_front.jpg',
  'id_card_back.png',
  'passport_scan.pdf',
  'license_001.jpg',
  'bank_statement_2024.pdf',
  'tax_form.png',
  'registration_doc.pdf',
  'medical_report.jpg',
  'invoice_12345.pdf',
  'receipt_scan.png',
  'contract_signed.pdf',
  'document_batch_01.pdf',
  'scan_20240115.jpg',
  'upload_file.png',
  'ocr_test.pdf',
];

// Error messages for failed requests
const ERROR_MESSAGES = [
  'Image quality too low',
  'Document type not supported',
  'Text extraction failed',
  'Request timeout',
  'Invalid file format',
  'File size exceeds limit',
];

// Helper to generate a random UUID-like ID
const generateRequestId = () => {
  const chars = 'abcdef0123456789';
  let result = '';
  for (let i = 0; i < 32; i++) {
    if (i === 8 || i === 12 || i === 16 || i === 20) {
      result += '-';
    }
    result += chars[Math.floor(Math.random() * chars.length)];
  }
  return result;
};

// Generate a single mock activity log entry
export const generateMockActivityLog = (
  timestamp: dayjs.Dayjs,
  forceError = false
): OCRActivityLog => {
  const isSuccess = forceError ? false : Math.random() > 0.05; // 95% success rate
  const documentType =
    DOCUMENT_TYPES[Math.floor(Math.random() * DOCUMENT_TYPES.length)];
  const fileName = FILE_NAMES[Math.floor(Math.random() * FILE_NAMES.length)];

  const log: OCRActivityLog = {
    id: generateRequestId(),
    timestamp: timestamp.toISOString(),
    request_id: `req_${generateRequestId().slice(0, 16)}`,
    status: isSuccess ? 'success' : 'error',
    latency_ms: isSuccess
      ? Math.floor(Math.random() * 600) + 150
      : Math.floor(Math.random() * 200) + 50,
    tokens_used: isSuccess ? Math.floor(Math.random() * 400) + 100 : 0,
    document_type: documentType,
    file_name: fileName,
  };

  if (!isSuccess) {
    log.error_message =
      ERROR_MESSAGES[Math.floor(Math.random() * ERROR_MESSAGES.length)];
  }

  return log;
};

// Generate multiple mock activity logs
export const generateMockActivityLogs = (
  count: number,
  startDate?: dayjs.Dayjs
): OCRActivityLog[] => {
  const logs: OCRActivityLog[] = [];
  const start = startDate || dayjs().subtract(30, 'day');

  for (let i = 0; i < count; i++) {
    // Generate timestamps spread across the time range
    const randomMinutes = Math.floor(Math.random() * 30 * 24 * 60);
    const timestamp = start.add(randomMinutes, 'minute');
    logs.push(generateMockActivityLog(timestamp));
  }

  // Sort by timestamp descending (most recent first)
  logs.sort((a, b) => dayjs(b.timestamp).unix() - dayjs(a.timestamp).unix());

  return logs;
};

// Generate paginated activity logs response
export const generateMockActivityLogsResponse = (
  page = 1,
  pageSize = 20,
  totalCount = 100
): OCRActivityLogsResponse => {
  // Generate all logs once (in a real app, this would be fetched from DB)
  const allLogs = generateMockActivityLogs(totalCount);

  // Calculate pagination
  const startIndex = (page - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalCount);
  const paginatedLogs = allLogs.slice(startIndex, endIndex);

  return {
    logs: paginatedLogs,
    total_count: totalCount,
    page,
    page_size: pageSize,
  };
};
